import { useMutation } from '@tanstack/react-query'
import { FormattedMessage, useIntl } from 'react-intl'

import { downloadInvoice } from '@/domains/Business/api'
import { useErrorToast } from '@/hooks/useErrorToast'
import { downloadFile } from '@/lib/utils'
import { DownloadFile, Ellipsis } from '@/shared/icons/solid'
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Spinner,
} from '@/shared/ui'

import { ContractorsTransaction } from '../types'

type Props = {
  payment: ContractorsTransaction
}

export const ContractorPaymentActions = ({ payment }: Props) => {
  const notifyError = useErrorToast()
  const intl = useIntl()

  const { mutateAsync, isPending } = useMutation({
    mutationFn: downloadInvoice,
  })

  const handleDownloadInvoice = async () => {
    if (!payment.transactionId) return

    try {
      const response = await mutateAsync({
        transactionId: payment.transactionId,
      })

      downloadFile(
        response.data,
        intl.formatMessage({
          id: 'contractor.payment.invoice.label',
          defaultMessage: 'invoice.pdf',
        }),
      )
    } catch (error) {
      notifyError(error)
    }
  }

  if (!payment.transactionId) return null

  return (
    <div className="flex items-center justify-end">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            aria-label="Open payment actions dropdown"
            size="inline"
            disabled={isPending}
            className="size-8 md:size-10"
            variant="ghost"
          >
            {isPending ? <Spinner /> : <Ellipsis />}
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end">
          <DropdownMenuItem asChild>
            <Button
              size="sm"
              variant="dropdown"
              leftIcon={<DownloadFile className="size-4" />}
              width="full"
              disabled={isPending}
              loading={isPending}
              onClick={(e) => {
                e.stopPropagation()
                handleDownloadInvoice()
              }}
            >
              <FormattedMessage
                id="action.downloadInvoice"
                defaultMessage="Download invoice"
              />
            </Button>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
